import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2, Plug, Lock, RefreshCw } from "lucide-react";
import DuprUatConsole from "@/components/DuprUatConsole";

const Dot = ({ on }) => <span className={`inline-block w-2 h-2 rounded-full ${on ? "bg-[#A3E635]" : "bg-red-500"}`} />;

export default function DuprPanel() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => api.get("/admin/dupr/status").then(({ data }) => { setSt(data); setResult(data.last_test); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/dupr/connection-test");
      setResult(data);
      data.ok ? toast.success(`DUPR ${data.env.toUpperCase()} handshake succeeded`) : toast.error(`DUPR handshake failed: ${data.error}`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!st) return <div className="text-xs text-zinc-500" data-testid="dupr-panel-loading">Loading DUPR status…</div>;
  const prod = st.env === "production";
  return (
    <div className="space-y-4" data-testid="dupr-panel">
      <div className="flex flex-wrap items-center gap-3">
        <span data-testid="dupr-env-badge" className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-black uppercase tracking-[0.25em] border ${prod ? "border-red-500 text-red-400 bg-red-500/10" : "border-pb-cyan text-pb-cyan bg-pb-cyan/10"}`}>
          {prod ? <ShieldAlert className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />} {st.env} environment
        </span>
        {st.production_locked && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500" data-testid="dupr-prod-locked"><Lock className="w-3 h-3" /> Production locked</span>}
        <span className="font-mono text-[11px] text-zinc-400" data-testid="dupr-base-url">{st.token_endpoint}</span>
        <button data-testid="dupr-refresh" onClick={load} className="ml-auto w-7 h-7 flex items-center justify-center border border-pb-border hover:border-pb-red"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid sm:grid-cols-4 gap-2" data-testid="dupr-config">
        {Object.entries(st.configured).map(([k, v]) => (
          <div key={k} className="border border-pb-border/60 bg-black/30 px-3 py-2 flex items-center justify-between text-[11px]" data-testid={`dupr-key-${k.toLowerCase()}`}>
            <span className="font-mono text-zinc-300">{k}</span><span className="flex items-center gap-1.5 text-zinc-500"><Dot on={v} />{v ? "set" : "missing"}</span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-zinc-500">Values are never shown or sent to the browser — only whether each key is present. The test below requests a partner access token from DUPR {st.env.toUpperCase()}; it reads nothing and writes nothing on DUPR.</p>
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="dupr-test-btn" onClick={test} disabled={busy || !st.ready}
          className="inline-flex items-center gap-2 bg-pb-cyan text-black px-4 py-2.5 text-xs font-black uppercase tracking-widest hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Test DUPR connection
        </button>
        {!st.ready && <span className="text-[11px] text-amber-400" data-testid="dupr-not-ready">Add DUPR_CLIENT_KEY and DUPR_CLIENT_SECRET in Secrets to enable the test.</span>}
      </div>
      {result && (
        <div data-testid="dupr-test-result" className={`border p-3 text-xs font-mono space-y-1 ${result.ok ? "border-[#A3E635]/50 bg-[#A3E635]/5" : "border-red-500/50 bg-red-500/5"}`}>
          <div className={result.ok ? "text-[#A3E635] font-black" : "text-red-400 font-black"} data-testid="dupr-test-outcome">{result.ok ? "CONNECTED — access token issued" : "FAILED"}{result.status_code ? ` · HTTP ${result.status_code}` : ""}</div>
          <div className="text-zinc-400">env {result.env} · {result.base_url}</div>
          {result.request_id && <div className="text-zinc-400">X-Request-Id {result.request_id}</div>}
          {result.expiry && <div className="text-zinc-400">token expiry {result.expiry}</div>}
          {result.error && <div className="text-red-300" data-testid="dupr-test-error">{result.error}</div>}
          <div className="text-zinc-600">{new Date(result.at).toLocaleString()} · {result.by}</div>
        </div>
      )}
      <div className="mt-5 border border-pb-border/60 bg-black/30 px-4 py-3 flex items-center justify-between gap-4" data-testid="dupr-connect-toggle-row">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-white">Show "Connect DUPR" to players</div>
          <div className="text-[10px] text-zinc-500 mt-0.5" data-testid="dupr-connect-toggle-help">
            {st.connect_enabled
              ? "ON — players link through DUPR's official sign-in; manual DUPR entry is closed (DUPR-compliant)."
              : "OFF — Connect button hidden; the profile field reverts to manual DUPR / VAIR entry (not accepted for DUPR's review)."}
          </div>
        </div>
        <button type="button" data-testid="dupr-connect-toggle" role="switch" aria-checked={!!st.connect_enabled} disabled={toggling}
          onClick={async () => {
            setToggling(true);
            try { const { data } = await api.put("/admin/dupr/settings", { connect_enabled: !st.connect_enabled }); setSt((s) => ({ ...s, connect_enabled: data.connect_enabled || !data.manual_dupr_allowed })); toast.success(data.manual_dupr_allowed ? "Connect DUPR hidden — manual entry restored" : "Connect DUPR visible to players"); }
            catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
            finally { setToggling(false); }
          }}
          className={`relative w-12 h-6 rounded-full shrink-0 transition-colors disabled:opacity-50 ${st.connect_enabled ? "bg-pb-cyan" : "bg-zinc-700"}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all duration-200 ${st.connect_enabled ? "left-[26px]" : "left-0.5"}`} />
        </button>
      </div>
      <DuprUatConsole env={st.env} ssoEnabled={st.sso_enabled} />
    </div>
  );
}
